import { useLayoutEffect, useRef, useState } from 'react';
import { placePopup } from '../utils/popupPlacement';

/**
 * Keeping something drawn AT A POINT — a cursor tooltip, a click-positioned
 * popup — inside the window.
 *
 * `usePopoverPosition` is the other half of this problem and does not fit:
 * it measures an anchor ELEMENT of known width and aligns to its edges. Here
 * there is no anchor, only coordinates, and the thing being placed is of
 * unknown size until it has rendered once.
 *
 * So it renders at the raw point, measures, and moves — in a layout effect, so
 * the move happens before paint and nothing flickers. jsdom measures zero and
 * simply keeps the raw point, which is what its tests already assume.
 *
 * @param {{x: number, y: number}|null} point  null when nothing is shown
 * @returns {[React.RefObject, {left: number, top: number}|null]}
 */
export default function usePointPlacement(point) {
  const ref = useRef(null);
  const [place, setPlace] = useState(null);
  const x = point?.x;
  const y = point?.y;

  useLayoutEffect(() => {
    if (x == null || y == null) {
      setPlace(null);
      return undefined;
    }
    const apply = () => {
      const node = ref.current;
      const rect = node?.getBoundingClientRect();
      // Not laid out yet (or jsdom): the raw point is the honest answer.
      if (!rect?.width || !rect?.height) {
        setPlace({ left: x, top: y });
        return;
      }
      setPlace(
        placePopup({
          x,
          y,
          width: rect.width,
          height: rect.height,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
        })
      );
    };
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [x, y]);

  return [ref, place];
}
